import { Directive, Input, OnInit } from '@angular/core'
import {
  ActionFailed,
  DataCheckerService,
  Destroyable,
  Rental,
  SaveForm,
  Toaster,
  htmlToPlainText,
  selectRentalById,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Validators, FormBuilder, FormGroup } from '@angular/forms'
import { finalize, map } from 'rxjs/operators'
import { Store, select } from '@ngrx/store'
import * as moment from 'moment'
import { selectTemplatesByTypes, TemplateType, Template } from '@tokeet-frontend/templates'
import { DataDictService } from '@tv3/services/data-dict.service'
import { TemplateGuard } from '@tv3/guards/template.guard'
import {
  ChannelReviewItem,
  getChannelReviewSource,
  isGuestReviewable,
  isGuestReviewRepliable,
  isReviewGuestExpired,
  ReviewService,
} from '@tokeet-frontend/reviews'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'

@Directive()
export abstract class BookingReviewBaseComponent extends Destroyable implements OnInit {
  @Input() inquiry: Inquiry

  get reviewSource() {
    return getChannelReviewSource(this.inquiry.inquirySource)
  }

  rental: Rental
  currentDate = moment.utc().endOf('date').unix()

  reviewToGuest: ChannelReviewItem
  reviewFromGuest: ChannelReviewItem

  replyForm = this.fb.group({
    message: ['', [Validators.required]],
  })
  isLoading = false
  isRespondable = false
  isReviewable = false
  isReviewToGuestExpired = false
  isGeneratingAIresponse = false

  templates$ = this.store.pipe(select(selectTemplatesByTypes([TemplateType.Review])))
  aiResponderSub$ = this.store.pipe(select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)))

  constructor(
    protected fb: FormBuilder,
    protected store: Store<any>,
    protected reviewService: ReviewService,
    protected toaster: Toaster,
    protected dataDictService: DataDictService,
    protected dataChecker: DataCheckerService
  ) {
    super()
    dataChecker.check([TemplateGuard])
  }

  ngOnInit(): void {
    this.store.pipe(select(selectRentalById(this.inquiry.rentalId)), untilDestroy(this)).subscribe((rental) => {
      this.rental = rental
    })

    this.loadReviews()
  }

  loadReviews() {
    this.reviewService.getBookingReviews(this.inquiry.id, [this.reviewSource]).subscribe(
      (data) => {
        this.reviewFromGuest = data.reviewFromGuest
        this.reviewToGuest = data.reviewToGuest
        this.isReviewToGuestExpired = isReviewGuestExpired(this.reviewToGuest)

        this.isRespondable = isGuestReviewRepliable(this.reviewFromGuest)
        this.isReviewable = isGuestReviewable(this.reviewToGuest)
      },
      (error) => {
        // this.toaster.error('Unable to load review for this booking', '', error)
        console.error(error)
      }
    )
  }

  onResponseTemplateChange([template]: [Template]) {
    this.getTemplateMessage(template).subscribe((compiledMessage) => {
      this.replyForm.patchValue({ message: compiledMessage })
    })
  }

  private getTemplateMessage(template: Template) {
    const inquiry = this.inquiry
    return this.dataDictService
      .resolve(template.body, {
        inquiry: inquiry,
        rental: inquiry.rental,
        guest: inquiry.guest,
      })
      .pipe(map((m) => htmlToPlainText(m)))
  }

  abstract onReviewGuest(): void

  @SaveForm()
  onReply(form: FormGroup) {
    const { message } = form.getRawValue()

    this.isLoading = true
    this.reviewService
      .respondReview(this.reviewFromGuest._id, message)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.loadReviews()
          this.toaster.success($localize`Response saved successfully`)
        },
        (error) => {
          this.toaster.error($localize`Unable to save response`, null, error)
        }
      )
  }

  onAIResponse() {
    this.isGeneratingAIresponse = true
    const review = this.reviewFromGuest
    this.reviewService
      .aiGuestReviewResponse({
        account: this.inquiry.account,
        inquiryId: review.inquiryId,
        review: review.text,
        categories: review.categoryRatings?.map((c) => ({
          type: c.category,
          // comment: c.comment || '',
          stars: c.rating,
          // tags: c.review_category_tags || [],
        })),
      })
      .pipe(finalize(() => (this.isGeneratingAIresponse = false)))
      .subscribe(
        (message) => {
          this.replyForm.patchValue({ message })
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
